<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaButton from '../base/GaiaButton.vue';
import GaiaStatusIndicator from '../base/GaiaStatusIndicator.vue';
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral, gaiaStatus, gaiaTextButtonOverrides } from '../themes/tokens';

const props = defineProps({
  /**
   * confirm = destructive inline confirm (Cancel + danger action),
   * busy = operation in progress (spinner + right-hand note),
   * error = failed operation (retry text-button + dismiss),
   * plain = neutral note strip (e.g. "No files on this contract yet").
   */
  variant: {
    type: String as PropType<'confirm' | 'busy' | 'error' | 'plain'>,
    required: true,
  },
  /** Indented child strip under an expanded table row (contract file list). */
  indent: {
    type: Boolean,
    default: false,
  },
  /** Bold lead-in on the confirm variant, e.g. `Delete "Doruk"?`. */
  title: {
    type: String,
    default: undefined,
  },
  confirmLabel: {
    type: String,
    default: 'Delete',
  },
  cancelLabel: {
    type: String,
    default: 'Cancel',
  },
  retryLabel: {
    type: String,
    default: 'Try again',
  },
  dismissLabel: {
    type: String,
    default: 'Dismiss',
  },
  /** Muted right-hand note on the busy variant. */
  note: {
    type: String,
    default: undefined,
  },
});

const emit = defineEmits<{ confirm: []; cancel: []; retry: []; dismiss: [] }>();

// The retry text-button sits on the danger surface — color to match.
const retryOverrides = gaiaTextButtonOverrides.danger;

const alertSurface = gaiaStatus.alertSurface;
const childSurface = gaiaNeutral.surfaceChild;
const alertBar = gaiaStatus.alert;
const busySurface = gaiaNeutral.surfaceSoft;
const rowBorder = gaiaNeutral.borderSoft;
const spinnerColor = gaiaNeutral.textSubtle;

// Size and tone ride GaiaText's inline style channel, so the per-variant text
// treatment is computed here rather than expressed as CSS variant selectors.
const textSize = computed(() => (props.variant === 'plain' ? 'dense' : 'body'));
const textTone = computed(() => {
  if (props.variant === 'plain') return 'subtle';
  return props.variant === 'busy' ? 'tertiary' : 'text';
});
</script>

<template>
  <!-- Inline row-state strip from the CM list mock: replaces a table
       row while a destructive action is confirmed / running / failed. Naive
       has no row-replacement primitive, so this is self-contained; colors
       come from the shared tokens, buttons are GaiaButtons. -->
  <div
    class="gaia-table-row-state"
    :class="[`gaia-table-row-state--${props.variant}`, { indent: props.indent }]"
  >
    <n-flex align="center" :size="10" :wrap="false" :style="{ minWidth: 0 }">
      <svg
        v-if="props.variant === 'busy'"
        class="spinner"
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        :stroke="spinnerColor"
        stroke-width="1.6"
        stroke-linecap="round"
      >
        <path d="M6 1.2A4.8 4.8 0 1 1 1.2 6" />
      </svg>
      <gaia-status-indicator v-else-if="props.variant !== 'plain'" status="alert" />
      <gaia-text truncate :size="textSize" :tone="textTone">
        <gaia-text
          v-if="props.title"
          weight="semibold"
          tone="ink"
          :style="{ marginRight: '4px' }"
          >{{ props.title }}</gaia-text
        >
        <slot />
      </gaia-text>
    </n-flex>
    <n-flex align="center" :size="8" :wrap="false">
      <slot v-if="$slots.actions" name="actions" />
      <template v-else-if="props.variant === 'confirm'">
        <gaia-button size="small" @click="emit('cancel')">{{ props.cancelLabel }}</gaia-button>
        <gaia-button size="small" type="error" @click="emit('confirm')">
          {{ props.confirmLabel }}
        </gaia-button>
      </template>
      <gaia-text v-else-if="props.variant === 'busy'" size="control" tone="muted">
        {{ props.note }}
      </gaia-text>
      <template v-else-if="props.variant === 'error'">
        <gaia-button text size="small" :theme-overrides="retryOverrides" @click="emit('retry')">
          {{ props.retryLabel }}
        </gaia-button>
        <gaia-button size="small" @click="emit('dismiss')">{{ props.dismissLabel }}</gaia-button>
      </template>
    </n-flex>
  </div>
</template>

<style scoped>
.gaia-table-row-state {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 0 18px;
  height: 56px;
  border-bottom: 1px solid v-bind(rowBorder);
}

.gaia-table-row-state--confirm,
.gaia-table-row-state--error {
  background: v-bind(alertSurface);
  box-shadow: inset 2px 0 0 v-bind(alertBar);
}

.gaia-table-row-state--busy {
  background: v-bind(busySurface);
}

.gaia-table-row-state--plain {
  background: v-bind(childSurface);
}

/* Child strip under an expanded row: indented past checkbox + expander. */
.gaia-table-row-state.indent {
  padding-left: 84px;
  height: 52px;
}

.gaia-table-row-state.indent.gaia-table-row-state--busy {
  background: v-bind(childSurface);
}

.spinner {
  flex: none;
  animation: gaia-row-spin 800ms linear infinite;
}

@keyframes gaia-row-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
